import type { TRootState } from "@/reducers";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import type { ApolloError, FetchResult, LazyQueryExecFunction, OperationVariables } from "@apollo/client";
import { datadogLogs } from "@datadog/browser-logs";
import {
  customerAccessTokenCreateMutation,
  customerCreateMutation,
  customerQuery,
  customerUpdateMutation,
} from "@repo/shopify/src/queries/customer-gql";
import type {
  ICustomerAccessTokenCreateMutation,
  ICustomerCreateMutation,
  ICustomerQuery,
  ICustomerUpdateMutation,
  IOrdersEdges,
} from "@repo/shopify/src/queries/customer-gql";
import type { CustomerAccessTokenCreateInput, CustomerCreateInput } from "@shopify/hydrogen-react/storefront-api-types";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import type { IUserState } from "@reducers/user";
import { userCreator } from "@reducers/user";

import useEventSession from "@hooks/reducerHooks/useEventSession";
import useLayout from "@hooks/reducerHooks/useLayout";

import { ErrorCode } from "@const/enum";
import { URLS } from "@const/urls";

import { NOTIFICATIONS } from "@utils/notifications";

interface IUser {
  customer: ICustomerQuery["customer"];
  user: IUserState;
  accessToken: string;
  userLogout: () => void;
  updateOrders: (orders: IOrdersEdges) => void;
  userLogin: (accessToken: string) => void;
  updateUser: (user: IUserState) => void;
  fetchUser: LazyQueryExecFunction<ICustomerQuery, OperationVariables>;
  loginLoading: boolean;
  loginError: ApolloError | undefined;
  changePassword: (options: {
    variables: { customerAccessToken: string; customer: { password: string } };
  }) => Promise<FetchResult<ICustomerUpdateMutation>>;
  createCustomer: (options: {
    variables: { input: CustomerCreateInput };
  }) => Promise<FetchResult<ICustomerCreateMutation>>;
  createToken: (options: {
    variables: { input: CustomerAccessTokenCreateInput };
    onCompleted?: (data: ICustomerAccessTokenCreateMutation) => void;
    onError?: (error: Error) => void;
  }) => Promise<FetchResult<ICustomerAccessTokenCreateMutation>>;
  createCustomerLoading: boolean;
}

function useUser(): IUser {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { openToast } = useLayout();
  const { updateEventSession } = useEventSession();

  const user = useSelector((state: TRootState) => state.user);
  const accessToken = useSelector((state: TRootState) => state.user.accessToken);

  const userLogout = useCallback(() => {
    updateEventSession([]);
    dispatch(userCreator.userLogout());
  }, []);

  const updateOrders = useCallback((orders: IOrdersEdges) => {
    dispatch(userCreator.updateOrders(orders));
  }, []);

  const userLogin = useCallback((accessToken: string) => {
    dispatch(userCreator.userLogin({ ...user, accessToken }));
  }, []);

  const updateUser = useCallback((data: IUserState) => {
    dispatch(userCreator.updateUser({ ...user, ...data }));
  }, []);

  const [fetchUser, { loading: loginLoading, error: loginError }] = useLazyQuery<ICustomerQuery>(
    gql(customerQuery.query),
    {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (data.customer) {
          updateUser({ ...data, accessToken });
          updateOrders(data.customer.orders ?? []);
        }
        return data.customer;
      },
      onError: (error) => {
        datadogLogs.logger.error("[SHOPIFY][Customer] Failed to query", error);
        throw new Error(`[SHOPIFY][Customer] Failed to query : ${JSON.stringify(error)}`);
      },
    },
  );

  const [changePassword] = useMutation<ICustomerUpdateMutation>(gql(customerUpdateMutation.query), {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data.customerUpdate.customerUserErrors.length) {
        return;
      }

      const newAccessToken = data.customerUpdate.customerAccessToken?.accessToken;
      userLogin(newAccessToken || "");
      openToast(NOTIFICATIONS.SUCCESS_PASSWORD_CHANGED);
    },
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][CustomerUpdate] Failed to mutation", error);
      throw new Error(`[SHOPIFY][CustomerUpdate] Failed to mutation : ${JSON.stringify(error)}`);
    },
  });

  const [createCustomer, { loading: createCustomerLoading }] = useMutation<ICustomerCreateMutation>(
    gql(customerCreateMutation.query),
    {
      onCompleted: (data) => {
        data.customerCreate.customerUserErrors.forEach((error) => {
          if (error.code === ErrorCode.UnidentifiedCustomer) {
            openToast(NOTIFICATIONS.WARN_INVALID_CREDENTIALS);
            navigate(URLS.HOME, { replace: true });
          } else if (error.code === ErrorCode.Taken) {
            openToast(NOTIFICATIONS.WARN_ACCOUNT_TAKEN);
          } else if (error.code === ErrorCode.TooShort) {
            openToast(NOTIFICATIONS.WARN_TOO_SHORT);
          } else {
            openToast(NOTIFICATIONS.ERROR_DEFAULT);
          }
        });
      },
      onError: (error) => {
        openToast(NOTIFICATIONS.ERROR_REGISTER_LIMIT);
        datadogLogs.logger.error("[SHOPIFY][CustomerCreate] Failed to mutation", error);
        throw new Error(`[SHOPIFY][CustomerCreate] Failed to mutation : ${JSON.stringify(error)}`);
      },
    },
  );

  const [createToken] = useMutation<ICustomerAccessTokenCreateMutation>(gql(customerAccessTokenCreateMutation.query), {
    onCompleted: (data) => {
      if (data.customerAccessTokenCreate.customerUserErrors.length) {
        data.customerAccessTokenCreate.customerUserErrors.forEach((error) => {
          if (error.code === ErrorCode.UnidentifiedCustomer) {
            openToast(NOTIFICATIONS.WARN_INVALID_CREDENTIALS);
            navigate(URLS.HOME, { replace: true });
          } else if (error.code === ErrorCode.Taken) {
            openToast(NOTIFICATIONS.WARN_ACCOUNT_TAKEN);
          } else if (error.code === ErrorCode.TooShort) {
            openToast(NOTIFICATIONS.WARN_TOO_SHORT);
          } else {
            openToast(NOTIFICATIONS.ERROR_DEFAULT);
          }
        });
        return;
      }

      const accessToken = data.customerAccessTokenCreate.customerAccessToken?.accessToken;

      if (accessToken) {
        /** accessToken 전달받아 로그인 처리, customer 재조회하여 저장 */
        if (loginLoading || loginError) return;

        /**
         * NOTE: fetchUser는 accessToken을 이용하여 customer 를 조회.
         * fetchUser의 onCompleted 콜백에서 customer 정보를 redux user에 저장하는데,
         * fetchUser가 완료되기 전에 컴포넌트가 unmount 되면서 onCompleted 콜백이 호출되지 않음.
         * => App.jsx accessToken 있는지 확인해서 페이지를 렌더링. 여기서 fetchUser 완료 전에 로그인 페이지 unmount
         *
         * accessToken 을 redux user에 저장(userLogin)하는 순서를 fetchUser 완료한 후에 하도록 수정
         */

        // 넘겨받은 accessToken으로 customer 정보 query 조회. fetchUser 함수 성공 콜백에서 customer 데이터를 redux user에 저장
        fetchUser({ variables: { customerAccessToken: accessToken } }).then(() => {
          // fetchUser 완료된 후 accessToken을 redux user에 저장
          userLogin(accessToken);
        });
      } else {
        datadogLogs.logger.error("Failed to Login", data);
        throw new Error("로그인 과정에서 오류가 발생했습니다.\n채널톡으로 문의해 주세요.");
      }
    },
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][CustomerAccessTokenCreate] Failed to mutation", error);
      throw new Error(`[SHOPIFY][CustomerAccessTokenCreate] Failed to mutation : ${JSON.stringify(error)}`);
    },
  });

  return {
    customer: user.customer,
    accessToken,
    user,
    userLogout,
    updateOrders,
    userLogin,
    updateUser,
    fetchUser,
    loginLoading,
    loginError,
    changePassword,
    createCustomer,
    createToken,
    createCustomerLoading,
  };
}

export default useUser;
